import { motion } from "framer-motion";
import { Palette, Building2, Waves, ArrowRight } from "lucide-react";
import { useT } from "../i18n/LanguageContext";

const COLLAGE =
    "https://customer-assets.emergentagent.com/job_wynwood-short-term/artifacts/wlhomf8b_ChatGPT%20Image%2021%20abr%202026%2C%2021_58_19.png";

export default function MiamiAttractions() {
    const { t } = useT();

    const districts = [
        {
            id: "wynwood",
            icon: Palette,
            title: t("attractions.wynwood_title"),
            body: t("attractions.wynwood_body"),
            tag: t("attractions.wynwood_tag"),
        },
        {
            id: "edgewater",
            icon: Building2,
            title: t("attractions.edgewater_title"),
            body: t("attractions.edgewater_body"),
            tag: t("attractions.edgewater_tag"),
        },
        {
            id: "miamibeach",
            icon: Waves,
            title: t("attractions.miamibeach_title"),
            body: t("attractions.miamibeach_body"),
            tag: t("attractions.miamibeach_tag"),
        },
    ];

    return (
        <section
            id="attractions"
            className="relative py-24 md:py-32 bg-charcoal-surface overflow-hidden"
            data-testid="attractions-section"
        >
            <div className="mx-auto max-w-7xl px-6 md:px-12">
                {/* Heading */}
                <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-12">
                    <div>
                        <div className="flex items-center gap-3 mb-5">
                            <span className="inline-block w-8 h-[1px] bg-gold" />
                            <span className="text-[11px] tracking-[0.4em] uppercase text-gold font-semibold">
                                {t("attractions.overline")}
                            </span>
                        </div>
                        <h2 className="font-display text-3xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.02]">
                            <span className="text-gold">
                                {t("attractions.heading_line1")}
                            </span>
                            <br />
                            {t("attractions.heading_line2")}
                        </h2>
                    </div>
                    <p className="text-white/60 max-w-md text-sm md:text-base leading-relaxed">
                        {t("attractions.subhead")}
                    </p>
                </div>

                {/* Stat bar */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                    className="grid grid-cols-1 md:grid-cols-2 border-y border-white/10 divide-y md:divide-y-0 md:divide-x divide-white/10 mb-14"
                    data-testid="attractions-stats"
                >
                    <div className="p-8 md:p-10">
                        <div className="flex items-baseline gap-3">
                            <div className="font-display text-5xl md:text-7xl font-bold text-white tracking-tight">
                                28.2M
                            </div>
                            <div className="text-gold text-sm font-semibold">/ yr</div>
                        </div>
                        <div className="mt-3 text-[11px] tracking-[0.3em] uppercase text-white/60">
                            {t("attractions.stat1_label")}
                        </div>
                    </div>
                    <div className="p-8 md:p-10">
                        <div className="flex items-baseline gap-3">
                            <div className="font-display text-5xl md:text-7xl font-bold text-gold tracking-tight">
                                15M
                            </div>
                            <div className="text-gold text-sm font-semibold">/ yr</div>
                        </div>
                        <div className="mt-3 text-[11px] tracking-[0.3em] uppercase text-white/60">
                            {t("attractions.stat2_label")}
                        </div>
                    </div>
                </motion.div>

                {/* Collage visual */}
                <motion.figure
                    initial={{ opacity: 0, scale: 0.96 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.9 }}
                    className="relative overflow-hidden border border-white/10 mb-14"
                    data-testid="attractions-collage"
                >
                    <img
                        src={COLLAGE}
                        alt="Miami attractions — Wynwood, Edgewater and Miami Beach"
                        className="w-full h-auto object-cover"
                        loading="lazy"
                    />
                    <figcaption className="sr-only">
                        Miami attractions collage featuring Wynwood art district,
                        Edgewater bay views and Miami Beach coastline.
                    </figcaption>
                </motion.figure>

                {/* District cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    {districts.map((d, i) => {
                        const Icon = d.icon;
                        return (
                            <motion.article
                                key={d.id}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: i * 0.1 }}
                                className="group p-8 border border-white/10 bg-charcoal hover:border-gold/50 transition-colors"
                                data-testid={`district-card-${d.id}`}
                            >
                                <div className="flex items-start justify-between mb-8">
                                    <div className="w-11 h-11 flex items-center justify-center border border-gold/30 text-gold group-hover:bg-gold group-hover:text-charcoal transition-colors">
                                        <Icon size={18} strokeWidth={1.6} />
                                    </div>
                                    <span className="text-[10px] tracking-[0.3em] uppercase text-white/40">
                                        {d.tag}
                                    </span>
                                </div>
                                <h3 className="font-display text-xl md:text-2xl font-semibold tracking-tight leading-snug">
                                    {d.title}
                                </h3>
                                <p className="mt-4 text-sm text-white/60 leading-relaxed">
                                    {d.body}
                                </p>
                                <div className="mt-6 inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-gold opacity-0 group-hover:opacity-100 transition-opacity">
                                    {t("attractions.explore")}
                                    <ArrowRight size={12} />
                                </div>
                            </motion.article>
                        );
                    })}
                </div>

                <p className="mt-10 text-[11px] text-white/40 italic">
                    {t("attractions.source")}
                </p>
            </div>
        </section>
    );
}
